import type { FormField } from '../types/form';

/**
 * 表单工具函数集合
 */

/**
 * 创建必填规则
 */
export const requiredRule = (message: string) => ({
  required: true,
  message,
});

/**
 * 创建最大长度规则
 */
export const maxLengthRule = (max: number, message?: string) => ({
  max,
  message: message || `最多输入${max}个字符`,
});

/**
 * 创建最小长度规则
 */
export const minLengthRule = (min: number, message?: string) => ({
  min,
  message: message || `至少输入${min}个字符`,
});

/**
 * 创建邮箱验证规则
 */
export const emailRule = (message?: string) => ({
  type: 'email' as const,
  message: message || '请输入有效的邮箱地址',
});

/**
 * 创建URL验证规则
 */
export const urlRule = (message?: string) => ({
  type: 'url' as const,
  message: message || '请输入有效的URL',
});

/**
 * 创建数字范围规则
 */
export const rangeRule = (min: number, max: number, message?: string) => ({
  type: 'number' as const,
  min,
  max,
  message: message || `请输入${min}-${max}之间的数字`,
});

/**
 * 创建正则表达式规则
 */
export const patternRule = (pattern: RegExp, message: string) => ({
  pattern,
  message,
});

/**
 * 创建手机号验证规则
 */
export const phoneRule = (message?: string) => ({
  pattern: /^1[3-9]\d{9}$/,
  message: message || '请输入有效的手机号码',
});

/**
 * 创建身份证验证规则
 */
export const idCardRule = (message?: string) => ({
  pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
  message: message || '请输入有效的身份证号码',
});

/**
 * 快速创建表单字段
 */
export const createFormField = (
  name: string,
  label: string,
  type: FormField['type'],
  options?: Partial<FormField>
): FormField => ({
  name,
  label,
  type,
  ...options,
});

/**
 * 批量创建表单字段
 */
export interface QuickFieldConfig {
  name: string;
  label: string;
  type?: FormField['type'];
  required?: boolean;
  placeholder?: string;
  options?: FormField['options'];
}

export const createFormFields = (configs: QuickFieldConfig[]): FormField[] => {
  return configs.map(config => ({
    name: config.name,
    label: config.label,
    type: config.type || 'input',
    placeholder: config.placeholder || `请输入${config.label}`,
    rules: config.required ? [requiredRule(`请输入${config.label}`)] : undefined,
    options: config.options,
  }));
};

/**
 * 常用表单字段预设
 */
export const commonFields = {
  /**
   * 名称字段
   */
  name: (label: string = '名称', required: boolean = true): FormField => ({
    name: 'name',
    label,
    type: 'input',
    placeholder: `请输入${label}`,
    rules: required ? [requiredRule(`请输入${label}`)] : undefined,
    max: 50,
  }),

  /**
   * 描述字段
   */
  description: (label: string = '描述', required: boolean = false): FormField => ({
    name: 'description',
    label,
    type: 'textarea',
    placeholder: `请输入${label}`,
    rules: required ? [requiredRule(`请输入${label}`)] : undefined,
    rows: 4,
    max: 500,
  }),

  /**
   * 状态字段
   */
  status: (label: string = '状态', required: boolean = true): FormField => ({
    name: 'status',
    label,
    type: 'select',
    placeholder: `请选择${label}`,
    rules: required ? [requiredRule(`请选择${label}`)] : undefined,
    options: [
      { label: '启用', value: 'active' },
      { label: '禁用', value: 'inactive' },
    ],
  }),

  /**
   * 备注字段
   */
  remark: (label: string = '备注', required: boolean = false): FormField => ({
    name: 'remark',
    label,
    type: 'textarea',
    placeholder: `请输入${label}`,
    rules: required ? [requiredRule(`请输入${label}`)] : undefined,
    rows: 3,
    max: 200,
  }),

  /**
   * 排序字段
   */
  sort: (label: string = '排序', required: boolean = false): FormField => ({
    name: 'sort',
    label,
    type: 'number',
    placeholder: `请输入${label}`,
    rules: required ? [requiredRule(`请输入${label}`)] : undefined,
    min: 0,
    defaultValue: 0,
  }),

  /**
   * 邮箱字段
   */
  email: (label: string = '邮箱', required: boolean = true): FormField => ({
    name: 'email',
    label,
    type: 'input',
    placeholder: `请输入${label}`,
    rules: required 
      ? [requiredRule(`请输入${label}`), emailRule()] 
      : [emailRule()],
  }),

  /**
   * 手机号字段
   */
  phone: (label: string = '手机号', required: boolean = true): FormField => ({
    name: 'phone',
    label,
    type: 'input',
    placeholder: `请输入${label}`,
    rules: required 
      ? [requiredRule(`请输入${label}`), phoneRule()] 
      : [phoneRule()],
  }),
};

